import React, { useRef, useMemo, useContext, useState} from 'react';
import { Box, Skeleton, Alert, Typography } from '@mui/material';
import Chart, {
  ArgumentAxis,
  ValueAxis,
  Series,
  Legend,
  Tooltip,
  CommonSeriesSettings,
  Export,
  Title,
  Font,
} from 'devextreme-react/chart';
import { useEndReasonTypeData } from '../contexts/EndReasonTypeDataContext';
import { useDashboardChartView } from '../contexts/DashboardChartViewContext';
import { EndReasonTypeFilterControls } from './EndReasonTypeFilterControls';
import { DashboardCard, DashboardCardContext} from '../components/DashboardCard';
import { DashboardChartViewSelect } from '../components/DashboardChartViewSelect';
import { DASHBOARD_CHART_VIEW_CONFIG, DASHBOARD_DISTRIBUTION_COLORS, DashboardChartView, getDashboardDistributionColor } from '../types';

interface StackedChartDataPoint {
  year: string;
  [key: string]: string | number;
}

interface TooltipArg {
  argument: string;
  seriesName: string;
  value?: number;
  point?: {
    data?: any;
  };
}

const EndReasonTypeChartContent = ({ chartRef }: { chartRef: React.RefObject<Chart> }) => {
  const { data, loading, error, filter } = useEndReasonTypeData();
  const { isExpanded } = useContext(DashboardCardContext);
  const [chartView, setChartView] = useDashboardChartView('endReason');
  const seriesType = chartView === 'area' ? 'area'
    : chartView === 'spline' ? 'spline'
    : chartView === 'splineArea' ? 'splinearea'
    : chartView === 'line' ? 'line'
    : 'stackedbar';

  // Prepare metadata for export
  const exportMetadata = useMemo(() => {
    const yearsLabel = filter.schoolYears.length > 0
      ? filter.schoolYears.map(y => `${y}-${y + 1}`).join(', ')
      : 'All Years';
    
    return {
      title: 'End Reason Type Distribution',
      subtitle: `Years: ${yearsLabel}`
    };
  }, [filter.schoolYears]);

  // Memoized chart data preparation
  const { chartData, endReasonTypes } = useMemo(() => {
    if (!data || data.length === 0) {
      return { chartData: [], endReasonTypes: [] };
    }

    const uniqueYears = [...new Set(data.map((d) => d.year))].sort();
    const uniqueTypes = [...new Set(data.map((d) => d.endReasonTypeName))].sort();

    const mappedData: StackedChartDataPoint[] = uniqueYears.map((year) => {
      const yearData: StackedChartDataPoint = { year: `${year}-${year + 1}` };

      uniqueTypes.forEach((type) => {
        const item = data.find((d) => d.year === year && d.endReasonTypeName === type);
        yearData[type] = item?.percentageSubmitted || 0;
      });
      return yearData;
    });

    return { chartData: mappedData, endReasonTypes: uniqueTypes };
  }, [data]);

  if (loading) {
    return <Skeleton variant="rounded" height={400} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}><EndReasonTypeFilterControls /><DashboardChartViewSelect dashboard="endReason" value={chartView} onChange={setChartView} /></Box>

      {error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      ) : !data || data.length === 0 ? (
        <Typography align="center" color="textSecondary">
          No end reason type data available for selected years
        </Typography>
      ) : (
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <Chart
            ref={chartRef}
            dataSource={chartData}
            height="100%"
            rotated={chartView === 'horizontalBar'}
            palette={DASHBOARD_DISTRIBUTION_COLORS}
          >
            <Title text={exportMetadata.title} subtitle={exportMetadata.subtitle}>
              <Font size={18} weight={600} />
            </Title>
            <CommonSeriesSettings
              type={seriesType}
              barPadding={0.3}
            />
            <ArgumentAxis />
            <ValueAxis
              visualRange={{ startValue: 0, endValue: 100 }}
              tickInterval={10}
              title="Percentage (%)"
            />
            {endReasonTypes.map((type, index) => (
              <Series
                key={type}
                valueField={type}
                argumentField="year"
                name={type}
                color={getDashboardDistributionColor(index)}
                barWidth={100}
              />
            ))}
            <Legend visible={true} horizontalAlignment="center" verticalAlignment="bottom" />
            <Tooltip
              enabled={true}
              customizeTooltip={(arg: TooltipArg) => {
                const yearString = arg.argument;
                const year = parseInt(yearString.split('-')[0]);
                const endReasonType = arg.seriesName;

                const item = data.find((d) => d.year === year && d.endReasonTypeName === endReasonType);

                if (item) {
                  return {
                    text: `${endReasonType}: ${arg.value?.toFixed(1)}% (${item.studentCount}/${item.totalEnrolled} students)`,
                  };
                }

                return {
                  text: `${endReasonType}: ${arg.value?.toFixed(1)}%`,
                };
              }}
            />
            <Export enabled={isExpanded} />
          </Chart>
        </Box>
      )}
    </Box>
  );
};

export const EndReasonTypeChartCard = () => {
  const { filter } = useEndReasonTypeData();
  const chartRef = useRef<Chart>(null);

  const handleExport = () => {
    if (chartRef.current) {
      const yearsLabel = filter.schoolYears.length > 0
        ? filter.schoolYears.join('_')
        : 'AllYears';
      chartRef.current.instance.exportTo(`EndReasonType_${yearsLabel}`, 'png');
    }
  };

  return (
    <DashboardCard title="End Reason" onExport={handleExport}>
      <EndReasonTypeChartContent chartRef={chartRef} />
    </DashboardCard>
  );
};
